import React from "react";
import styled from "styled-components";
import { TabChild, Tabs, SearchBar, CancelIcon, SearchIcon, Dropdown } from "@wit/mpesa-ui-components";
import { useTranslation } from "react-i18next";
import UploadImagePlaceHolderIcon from "../icons/upload-image-placeholder.icon";
import { DropdownType } from "@wit/mpesa-ui-components/lib/components/dropdown/dropdown.component";

enum TabState {
  CATEGORY = 0,
  SEARCH = 1,
}

interface IIconPickerProps {
  setIconID(field: string, value: any, shouldValidate?: boolean): void;
  name: string;
  backgroundColor?: string;
  error: string | undefined;
  icons: IIcon[];
  icon: IIcon | undefined;
  isLoading: boolean;
  tabLabel?: string;
  displayBgColor?: string;
  defaultIcon?: string;
  inputPlaceholder?: string;
  displayPreviewBgColor?: boolean;
  borderColor?: string;
  activeBorderColor?: string;
}

export interface IIcon {
  id: string;
  label: string;
  base64: string;
}

/**
 * Component to be call in dropdown of icons
 * @param param0 Icon Options Interface
 */
const IconPicker = ({
  setIconID,
  name,
  backgroundColor = "white",
  error,
  icons,
  icon,
  isLoading,
  tabLabel = "iconCategories",
  displayBgColor,
  defaultIcon,
  inputPlaceholder,
  displayPreviewBgColor = false,
  borderColor,
  activeBorderColor,
}: IIconPickerProps) => {
  const [t] = useTranslation();
  const [searching, setSearching] = React.useState(false);
  const [availableIcons, setAvailableIcons] = React.useState<IIcon[]>([]);
  const [filterIcons, setFilterIcons] = React.useState<IIcon[]>([]);
  const [search, setSearch] = React.useState("");
  const [selectedTab, setSelectedTab] = React.useState(0);
  const [open, setOpen] = React.useState(false);
  const [selectedIcon, setSelectedIcon] = React.useState<IIcon>(
    icon
      ? icon
      : {
          id: "000",
          label: inputPlaceholder || t("commons.iconPlaceholder"),
          base64: defaultIcon
            ? defaultIcon
            : "data:image/jpeg;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAMAAADXqc3KAAAABGdBTUEAALGPC/xhBQAAAAFzUkdCAK7OHOkAAABCUExURUdwTP////////////////////////////////////////////////////////////////////////////////////VtkI8AAAAVdFJOUwAEL/YVfkKZDnE8HWLvj9R1uOLNVtGZGB8AAAClSURBVCjPndFJDsMgDAVQM5oZMnD/qzYJwQGlm/bv+E9CxgD8HRtrTyxDz0Qdwp7ejH2VrTXa2hPWZQaHiCesDNQE6oYNYJ/A3lBTWKtI8XXVFQWFwBAsORowgqYqNyzA3HFEAt1A+HbkBLIB9tfmDsfA17i6JYgOTD9TTSsBj2UG029VCbahz7TcHZ20qsdKAo8YHOfcv/7vXGQKnL3Ah/1L+1s+30kQlUuz75oAAAAASUVORK5CYII=",
        },
  );

  /**
   * function called when changes the tab
   * @param tabValue value of the tab selected
   */
  const controlledSetSelectedTab = (tabValue: number) => {
    setSelectedTab(tabValue);
    switch (tabValue) {
      case TabState.CATEGORY:
        setAvailableIcons(icons);
        break;
      case TabState.SEARCH:
        setSearching(true);
        setFilterIcons(icons);
        break;
    }
  };

  /**
   * Update icon selected
   * @param index index of icon choosen
   */
  const selectIcon = (index: number) => {
    if (filterIcons.length > 0 && selectedTab === TabState.SEARCH) {
      setIconID(name, filterIcons[index].id);
      setSelectedIcon(filterIcons[index]);
    } else {
      setIconID(name, availableIcons[index].id);
      setSelectedIcon(availableIcons[index]);
    }
    setOpen(false);
  };

  React.useEffect(() => {
    if (search.length > 0) {
      setFilterIcons(icons.filter(icon => icon.label.toLowerCase().includes(search.toLowerCase())));
    } else {
      setFilterIcons(availableIcons);
    }
  }, [search, setFilterIcons]);

  React.useEffect(() => {
    setAvailableIcons(availableIcons);
  }, [setFilterIcons]);

  React.useEffect(() => {
    if (icons.length > 0) {
      setAvailableIcons(icons);
    }
  }, [icons]);

  return (
    <Dropdown
      hasValue={false}
      isOpen={open}
      controlledToggleOpen={setOpen}
      label={
        <DropdownLabelContainer>
          <IconDropDownContainer
            icon={selectedIcon.base64}
            backgroundColor={displayPreviewBgColor ? backgroundColor : displayBgColor ? displayBgColor : "#aaaaaa"}
          />
          <ColorText>{selectedIcon.label}</ColorText>
        </DropdownLabelContainer>
      }
      dropdownType={DropdownType.RECTANGULAR_NORMAL}
      error={error}
    >
      <IconsDropdown>
        {!isLoading ? (
          !searching ? (
            <Tabs
              isSecondaryTabs={true}
              controlledSetSelectedTab={controlledSetSelectedTab}
              controlledSelectedTab={selectedTab}
            >
              <TabChild label={t(`commons.${tabLabel}`)}>
                <IconSelectorSection>
                  {availableIcons.map((icon, index) => (
                    <IconContainer
                      key={index}
                      logo={icon.base64}
                      onClick={() => selectIcon(index)}
                      selected={icon.id === selectedIcon.id}
                      backgroundColor={backgroundColor}
                      displayBgColor={displayBgColor}
                      borderColor={borderColor}
                      activeBorderColor={activeBorderColor}
                    />
                  ))}
                </IconSelectorSection>
              </TabChild>
              {
                //@ts-ignore
                <TabChild label={<SearchIcon width="19px" height="19px" style={{ stroke: "#999999" }} />}>
                  <IconSelectorSection>
                    {availableIcons.map((icon, index) => (
                      <IconContainer
                        key={index}
                        logo={icon.base64}
                        onClick={() => selectIcon(index)}
                        selected={icon.id === selectedIcon.id}
                        backgroundColor={backgroundColor}
                        displayBgColor={displayBgColor}
                        borderColor={borderColor}
                        activeBorderColor={activeBorderColor}
                      />
                    ))}
                  </IconSelectorSection>
                </TabChild>
              }
            </Tabs>
          ) : (
            <div>
              <CustomRow>
                <SearchBar
                  placeholderLabel={t("commons.searchIconFilter")}
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  clearValue={() => null}
                />
                <CancelIcon
                  width={"24px"}
                  height={"24px"}
                  style={{ stroke: "#999999", marginRight: "12px" }}
                  onClick={() => {
                    setSearching(false);
                    setSearch("");
                    controlledSetSelectedTab(0);
                  }}
                />
              </CustomRow>
              <IconSelectorSection>
                {filterIcons.length > 0 ? (
                  filterIcons.map((icon, index) => (
                    <IconContainer
                      key={index}
                      logo={icon.base64}
                      onClick={() => selectIcon(index)}
                      selected={icon.id === selectedIcon.id}
                      backgroundColor={backgroundColor}
                      displayBgColor={displayBgColor}
                      borderColor={borderColor}
                      activeBorderColor={activeBorderColor}
                    />
                  ))
                ) : (
                  <NoIconFound>
                    <IconNotFound>
                      <UploadImagePlaceHolderIcon />
                    </IconNotFound>
                    <LabelNotFound>{t("commons.noFoundIcons")}</LabelNotFound>
                  </NoIconFound>
                )}
              </IconSelectorSection>
            </div>
          )
        ) : (
          <LoadingIcons>
            <LabelLoading>{t("commons.loadingIcons")}</LabelLoading>
          </LoadingIcons>
        )}
      </IconsDropdown>
    </Dropdown>
  );
};

export default IconPicker;

const IconsDropdown = styled("div")`
  height: 201px;
  > div {
    height: 100%;
  }
  > div > div:first-child {
    display: flex;
    padding-top: 1vh;
    padding-left: 0.5vw;
    height: 36px;
    > div {
      width: 100%;
      display: flex;
      flex: none;
      > div:last-child {
        margin-left: auto;
        margin-right: 14px;
      }
    }
  }
  > div > div:last-child {
    margin-top: 0;
    height: 164px;
  }
`;

const IconSelectorSection = styled("div")`
  border-top: 1px solid #ebebeb;
  display: flex;
  align-content: flex-start;
  flex-wrap: wrap;
  height: 100%;
  padding: 10px;
  overflow-y: auto;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const IconContainer = styled("div")<{
  logo: string;
  selected: boolean;
  backgroundColor?: string;
  displayBgColor?: string;
  borderColor?: string;
  activeBorderColor?: string;
}>`
  background-image: ${props => (props.logo ? `url("${props.logo}")` : "none")};
  background-repeat: no-repeat;
  background-size: 24px 24px;
  background-position: center;
  background-color: ${props => (props.displayBgColor ? props.displayBgColor : "#aaaaaa")};
  stroke: #8d99a7;
  width: 32px;
  height: 32px;
  margin: 2px 2px;
  border-radius: 3px;
  ${props => (props.borderColor ? `border: 1px solid ${props.borderColor};` : null)}
  ${props =>
    props.selected
      ? `stroke: ${props.theme.palette.white};background-color: ${
          props.backgroundColor ? props.backgroundColor : props.theme.palette.vodafoneRed
        }; ${props.activeBorderColor ? `border: 1px solid ${props.activeBorderColor};` : null}`
      : null}
    :hover {
    stroke: ${props => props.theme.palette.white};
    background-color: ${props => (props.backgroundColor ? props.backgroundColor : props.theme.palette.vodafoneRed)};
    ${props => (props.activeBorderColor ? `border: 1px solid ${props.activeBorderColor};` : null)}
  }
`;

const CustomRow = styled("span")`
  display: flex;
  align-items: center;
  > div {
    > div {
      display: none;
    }
    border: none;
    border-radius: inherit;
    :hover {
      border: none;
    }
    > input {
      font-size: 14px;
    }
    > svg {
      stroke: #333333;
      width: 14px;
      height: 14px;
    }
  }
`;

const NoIconFound = styled("div")`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const LabelNotFound = styled("div")`
  margin-top: 1vh;
  font-family: Vodafone Rg;
  font-size: 14px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.36;
  letter-spacing: normal;
  text-align: center;
  color: ${props => props.theme.palette.midGrey};
`;

const LoadingIcons = styled("div")`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const LabelLoading = styled("span")`
  margin: 3vh;
  font-family: Vodafone Rg;
  font-size: 14px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.36;
  letter-spacing: normal;
  text-align: center;
  color: ${props => props.theme.palette.midGrey};
`;

const IconNotFound = styled("div")`
  svg {
    width: 36px;
    height: 36px;
  }
`;

const DropdownLabelContainer = styled("div")`
  display: flex;
  align-items: center;
  margin-left: -12px;
  > span {
    margin-left: 0.5vw;
  }
`;

const ColorText = styled("span")`
  font-family: Vodafone Rg;
  font-size: 16px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.31;
  letter-spacing: normal;
  color: ${props => props.theme.palette.midGrey};
`;

const IconDropDownContainer = styled("div")<{
  icon: string;
  backgroundColor: string;
}>`
  background-color: ${props => (props.backgroundColor ? `${props.backgroundColor}` : "#ffffff")};
  background-image: ${props => (props.icon ? `url("${props.icon}")` : "none")};
  background-repeat: no-repeat;
  background-size: 24px 24px;
  background-position: center;
  width: 37px;
  height: 34px;
  border-bottom-left-radius: 5px;
  border-top-left-radius: 5px;
  border-right: 1px solid #ebebeb;
`;
